import IcCareer from "@/assets/img/astro-map/ic-career.svg"
import IcHealth from "@/assets/img/astro-map/ic-health.svg"
import IcRomance from "@/assets/img/astro-map/ic-romance.svg"
import IcSuccess from "@/assets/img/astro-map/ic-success.svg"

/**
 * Coordinate system: everything below is expressed as a fraction (0→1) of the
 * Figma "Screen Container" frame (414×420, node Scanning_Frame_14) — the same
 * frame CosmicMapScene reproduces. `mx`/`my` are frame fractions, so the camera
 * can centre any element and the static (email) view frames them exactly like
 * the design. Derived directly from the Figma node positions.
 */

/** Camera zoom while focusing a pin (stage 2) vs. the overview (stages 1 & 3). */
export const Z_FOCUS = 2.6
export const Z_OVERVIEW = 1

/**
 * The world-map image placed inside the frame (Figma `Map` instance: x=-30,
 * y=38, 679.8×344 in the 414×420 frame). Wider than the frame, so it bleeds off
 * the sides and the scene clips it. Keeps the map's true aspect → pins track the
 * continents.
 */
export const MAP_RECT = {
  left: "-7.25%",
  top: "9.05%",
  width: "164.2%",
  height: "81.9%",
} as const

/**
 * The four power-location pins (Figma Scanning2). Order = the storyboard reveal
 * order; the stage-2 camera centres each one in turn.
 */
export const PINS = [
  { icon: IcSuccess, label: "SUCCESS", text: "text-gold", glow: "#ffc014", mx: 0.1671, my: 0.326 },
  { icon: IcCareer, label: "CAREER", text: "text-green", glow: "#7fe718", mx: 0.3185, my: 0.5389 },
  { icon: IcRomance, label: "ROMANCE", text: "text-peach", glow: "#ff537f", mx: 0.6896, my: 0.4635 },
  { icon: IcHealth, label: "HEALTH", text: "text-cyan", glow: "#50e3c2", mx: 0.795, my: 0.2601 },
] as const

/** The moon badge (Figma `MOON`, x=332 y=240 40×40) — lower-right of the map. */
export const MOON = { mx: 0.8502, my: 0.619 } as const

/**
 * The five planetary-line "meridians" drawn over the map in stage 3. Each is a
 * long curved arc that enters from the top-right and sweeps down through the
 * pins; it draws itself in via stroke-dashoffset. Paths live in the frame's
 * 414×420 viewBox. Order = the storyboard reveal order.
 */
export const LINES = [
  { d: "M443.5 61.4589C319.656 24.6566 67.2655 -21.6622 -20 343.498", color: "#ffc014" }, // yellow
  { d: "M444.5 61.3988C218.501 7.67481 81.9077 166.155 68 443.5", color: "#7fe718" }, // green
  { d: "M483.5 70.9997C269.824 8.48115 158.711 178.397 144.5 482", color: "#6738ff" }, // violet
  { d: "M525.5 71.4997C273.531 20.4442 246.5 233 228 470.999", color: "#ff537f" }, // red
  { d: "M598.998 66.4973C358.498 -7.78352 332 251.5 351.997 429.497", color: "#dfcbf5" }, // white
] as const

/**
 * Planet badges that appear one-by-one on the meridian lines in stage 3 (Figma
 * Frame_14 Featured Icons). `mx`/`my` are frame fractions fitted to sit on the
 * drawn lines; the glyphs are the planets' astrological symbols.
 */
export const PLANETS = [
  { sym: "♅", mx: 0.2923, my: 0.2167 }, // Uranus
  { sym: "♆", mx: 0.6232, my: 0.281 }, // Neptune
  { sym: "☉", mx: 0.5411, my: 0.3881 }, // Sun
  { sym: "♇", mx: 0.4179, my: 0.681 }, // Pluto
  { sym: "♀", mx: 0.587, my: 0.7738 }, // Venus
] as const

/**
 * The visual state of the cosmic map at one instant — the semantic inputs
 * {@link CosmicMapScene} needs to draw a frame. The scene owns all geometry
 * (the cover-fit scale + camera transform); callers only describe *what* to show.
 * The scanning step derives this live from its timeline; the email step passes
 * {@link FINAL_VIEW} to render the fully-formed map.
 */
export interface CosmicMapView {
  /** World-map opacity (0 before it fades in, ~0.85 once revealed). */
  mapOpacity: number
  /** The pin the camera is flying to (its halo blooms + it's centred), or null for the overview. */
  focus: (typeof PINS)[number] | null
  /** Overview drift target between pins (centred at {@link Z_OVERVIEW}, no halo), or null. */
  pan: { mx: number; my: number } | null
  /** Camera zoom: {@link Z_FOCUS} while focusing a pin, {@link Z_OVERVIEW} otherwise. */
  zoom: number
  /** Whether pin `i` has been revealed yet. */
  pinShown: (i: number) => boolean
  /** Stage 3 is on: planetary lines, planets and the moon draw/appear. */
  onLines: boolean
  /** Stage 1 scan line is sweeping the map. */
  scanActive: boolean
  /** Vertical position of the sweeping scan line (CSS top value). */
  scanTop: string
}

/** The fully-formed map (stage-3 end state) — used as the email screen's hero. */
export const FINAL_VIEW: CosmicMapView = {
  mapOpacity: 0.85,
  focus: null,
  pan: null,
  zoom: Z_OVERVIEW,
  pinShown: () => true,
  onLines: true,
  scanActive: false,
  scanTop: "0%",
}
